import { isMobileDevice } from '@/lib/utils';

/**
 * Standardized layout utility functions for consistent spacing across components
 */

/**
 * Calculate standardized top padding for page layouts
 * Ensures consistent header clearance across all pages
 */
export const getStandardTopPadding = () => {
  // Mobile web: Header clearance, desktop no padding
  if (isMobileDevice()) {
    return 'pt-[72px] lg:pt-0'; // Standard mobile clearance, none on desktop
  }
  
  return 'lg:pt-0'; // Desktop only
};

/**
 * Calculate standardized bottom padding for page layouts
 * Ensures consistent footer clearance across all pages
 */
export const getStandardBottomPadding = (options: {
  isAuthenticated?: boolean;
  isMobile?: boolean;
} = {}) => {
  const { isAuthenticated = false, isMobile = false } = options;
  
  // Mobile web: adequate padding when authenticated (bottom nav is shown)
  if (isMobile && isAuthenticated) {
    return 'pb-32'; // Clear the bottom navigation + extra buffer for safe area
  }
  
  // Mobile web unauthenticated or desktop
  if (isMobile) {
    return 'pb-6'; // Less padding when no bottom nav
  }
  
  return 'pb-4'; // Desktop default
};

/**
 * Get safe area classes conditionally
 * Only applies safe scroll classes when modals are closed
 */
export const getSafeAreaClasses = (isAnyModalOpen: boolean = false) => {
  return !isAnyModalOpen ? 'ios-safe-scroll' : '';
};

// Backwards compatibility alias
export const getIOSSafeAreaClasses = getSafeAreaClasses;

/**
 * Calculate lighter top padding for public marketing pages
 * Provides appropriate header clearance without excessive whitespace
 */
export const getPublicPageTopPadding = () => {
  // Mobile web: Lighter header clearance for marketing pages
  if (isMobileDevice()) {
    return 'pt-10 lg:pt-0'; // 40px padding on mobile web, none on desktop
  }
  
  return 'lg:pt-0'; // Desktop only
};

/**
 * Check if we should apply new user layout adjustments
 */
export const shouldApplyNewUserLayout = (options: {
  isAuthenticated?: boolean;
  isNewUser?: boolean;
  newUserLoading?: boolean;
  title?: string;
} = {}) => {
  const { isAuthenticated = false, isNewUser = false, newUserLoading = false, title = '' } = options;
  
  return {
    showNewUserStyling: isAuthenticated && isNewUser && !newUserLoading,
    hasEmptyTitle: !title || title.trim() === '',
  };
};
